// Wood recipe — plan §4.2: domainRepeat(axis=x) → per-plank cellID reseed →
// waves(radialDistToWarpedAxis) rings + fbm(anisotropic ≈20:1) grain + rare knots.
// Immutable built-in root (never mutate — extend/fork instead).
//
// This is the plan's most composite material, and everything it needs lives inside the ONE
// `domainRepeat` layer body: the board lattice, the per-board reseed, the warped-axis rings, the
// anisotropic grain, the knots, and the inter-board gap. It is a single layer rather than five
// because every one of those stages consumes BOARD-LOCAL coordinates, and the layer model gives
// each layer the same world-space `p` (a layer cannot hand a transformed p to the next layer).
// `domainWarp` and `radialDistToWarpedAxis` are therefore prelude helpers, not layers — see the
// PrimitiveRegistry note.
//
// SCALE CONTRACT:
//   boards  14 cm wide × 1.8 m long, staggered; ~4 mm plank crown, ~6 mm gap depth
//   rings   ~1.2 cm spacing — the dominant colour feature
//   grain   ~2 cm cells stretched 20:1 along the board — the fine streaking
//
// WEAKNESS (plan §4.3): wood is "excellent" for plank/decking faces but weak for END GRAIN and
// joinery — this recipe only models the face, which is the only thing a top-down view shows.
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";

export const WOOD_PLANKS_RECIPE_ID = "woodPlanks";

export const woodPlanksRecipe = new MaterialRecipe({
	id: WOOD_PLANKS_RECIPE_ID,
	name: "Wood planks",
	roughness: 0.45,
	params: [
		new ParamSchema({key: "board_width", label: "Board width (m)", kind: "scalar", min: 0.03, max: 1, default: 0.14}),
		new ParamSchema({key: "board_length", label: "Board length (m)", kind: "scalar", min: 0.2, max: 20, default: 1.8}),
		// 1 = fully random butt-joint offsets (decking); 0 = aligned joints (parquet-ish).
		new ParamSchema({key: "board_stagger", label: "Board stagger", kind: "scalar", min: 0, max: 1, default: 1}),
		new ParamSchema({key: "ring_wavelength", label: "Ring spacing (m)", kind: "scalar", min: 0.002, max: 0.2, default: 0.012}),
		// > 1 narrows the dark ring lines, which is what distinguishes wood's crisp growth rings
		// from marble's soft veining (same primitive underneath).
		new ParamSchema({key: "ring_sharpness", label: "Ring sharpness", kind: "scalar", min: 0.05, max: 8, default: 1.8}),
		new ParamSchema({key: "grain_scale", label: "Grain scale (m)", kind: "scalar", min: 0.002, max: 0.5, default: 0.02}),
		// The anisotropy IS the wood look: features are stretched ~20:1 along the board's length.
		new ParamSchema({key: "grain_aspect", label: "Grain anisotropy", kind: "scalar", min: 1, max: 50, default: 20}),
		// The ring-axis warp: this is what turns concentric-looking bands into the characteristic
		// "cathedral" figure where the sawn face crosses the growth cone at a shallow angle.
		new ParamSchema({key: "ring_warpScale", label: "Ring warp scale (m)", kind: "scalar", min: 0.02, max: 10, default: 0.6}),
		new ParamSchema({key: "ring_warpAmount", label: "Ring warp amount (m)", kind: "scalar", min: 0, max: 1, default: 0.06}),
		// t = 0 is the ring core / knot (darkest), t = 1 the pale latewood between rings.
		new ParamSchema({key: "wood_ramp", label: "Wood ramp", kind: "ramp", default: [0, 0.22, 0.12, 0.06, 0.35, 0.42, 0.25, 0.12, 0.7, 0.6, 0.4, 0.22, 1, 0.72, 0.53, 0.32]}),
		new ParamSchema({key: "gap_color", label: "Gap colour", kind: "color", default: [0.05, 0.04, 0.03]}),
		new ParamSchema({key: "gap_width", label: "Gap width (m)", kind: "scalar", min: 0, max: 0.03, default: 0.004}),
		new ParamSchema({key: "plank_height", label: "Plank crown (m)", kind: "scalar", min: 0, max: 0.05, default: 0.004}),
		new ParamSchema({key: "gap_depth", label: "Gap depth (m)", kind: "scalar", min: -0.05, max: 0, default: -0.006}),
		// "Rare knots" per the plan: a per-board probability, so a knot is a whole-board property
		// (at most one per board) rather than a scattered field.
		new ParamSchema({key: "knot_chance", label: "Knot chance per board", kind: "scalar", min: 0, max: 1, default: 0.06}),
		new ParamSchema({key: "knot_radius", label: "Knot radius (m)", kind: "scalar", min: 0, max: 0.1,
			// commented; disable knots for now (current impl. is too ugly)
			//default: 0.02,
			default: 0,
		}),
		new ParamSchema({key: "polish_scale", label: "Surface scale (m)", kind: "scalar", min: 0.002, max: 0.5, default: 0.03}),
		new ParamSchema({key: "polish_height", label: "Surface relief (m)", kind: "scalar", min: 0, max: 0.01, default: 0.0002}),
	],
	layers: [
		new MaterialLayer({
			id: "planks",
			primitive: "domainRepeat",
			args: {
				period: {ref: "board_width"},
				lengthPeriod: {ref: "board_length"},
				stagger: {ref: "board_stagger"},
				ringWavelength: {ref: "ring_wavelength"},
				ringSharpness: {ref: "ring_sharpness"},
				grainScale: {ref: "grain_scale"},
				grainAspect: {ref: "grain_aspect"},
				warpScale: {ref: "ring_warpScale"},
				warpAmount: {ref: "ring_warpAmount"},
				woodRamp: {ref: "wood_ramp"},
				gapColor: {ref: "gap_color"},
				gapWidth: {ref: "gap_width"},
				plankHeight: {ref: "plank_height"},
				gapDepth: {ref: "gap_depth"},
				knotChance: {ref: "knot_chance"},
				knotRadius: {ref: "knot_radius"},
			},
			blend: "over",
			contributes: "both",
		}),
		// A hair of surface relief so a planed board is not perfectly flat under the specular lobe.
		// Two orders of magnitude below the plank crown — it must not compete with the gap relief.
		new MaterialLayer({
			id: "surface",
			primitive: "fbm",
			args: {
				scale: {ref: "polish_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				heightScale: {ref: "polish_height"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
